import React, { FC } from 'react';
import { Article } from 't2-graphql-types';
import classNames from 'classnames';
import { getArticlesCardData } from '../ArticleCard/services/getArticlesCardData';
import { ListSection } from '../ListSection/ListSection';
import Typo from '../Typography/Typo';
import { PostCard } from '../PostCard/PostCard';
import { PostCardLoader } from '../PostCard/PostCardLoader';
import { TPostCardProps } from '../PostCard/types';
import { SectionListResults } from '../SectionListResults/SectionListResults';

type TPaginatedArticlesListProps = {
  listSection: {
    title?: string;
    className?: string;
    loading: boolean;
  };
  content: {
    component?: FC<TPostCardProps>;
    items: Article[];
    itemCount: number;
    emptyMessage: string;
    onItemClick?: (articleId: string) => void;
    refetch: Function;
    error: boolean;
    loading: boolean;
    fetchMore?: (variables: any) => Promise<any>;
    called?: boolean;
  };
  showCounter?: boolean;
  pageSize: number;
};

export const PaginatedArticlesList: FC<TPaginatedArticlesListProps> = ({
  listSection,
  content,
  showCounter = true,
  pageSize,
}) => {
  const Component = content.component || PostCard;
  const cardData = getArticlesCardData(content.items || []);

  const results = (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {cardData.map((item) => (
          <Component article={item} key={item.id} />
        ))}
      </div>
    </div>
  );

  const resultsPlaceholder = (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      {Array.from({ length: pageSize }, (_, index) => (
        <div key={index}>
          <PostCardLoader />
        </div>
      ))}
    </div>
  );

  const emptyResults = (
    <Typo variant="p2" className="flex mt-2 mb-4 md:mt-10 md:mb-16 col-span-full justify-self-center">
      {content.emptyMessage}
    </Typo>
  );

  return (
    <ListSection
      className={classNames('w-full col-span-full lg:col-start-2 lg:col-span-10 ', listSection.className)}
      titleClassName="mb-7"
      titleText={listSection.title}
      loading={listSection.loading}
      totalItems={content.itemCount}
      showCounter={showCounter}
    >
      <SectionListResults
        results={results}
        resultsPlaceholder={resultsPlaceholder}
        emptyResults={emptyResults}
        loading={content.loading}
        fetchMore={content.fetchMore}
        take={pageSize}
        resultsCount={content.itemCount}
        data={cardData}
        called={content.called || cardData.length > 0}
      />
    </ListSection>
  );
};
